import type { EventProps } from 'react-big-calendar';
import { useMemo } from 'react';
import { differenceInMinutes } from 'date-fns';
import { TourDuration, TourStatusForZcheduler } from '@zg-rentals/ts-z-calendar-client';
import { Box, Paragraph } from '@zillow/constellation-10';

import { getTourEventTimeRange } from '../../../../utils/formatTime';
import { getEventTileHeightPx } from '../../../../utils/calendarUtils';
import { type TourEvent, Z_CALENDAR_EVENT_TYPE, type ZCalendarEvent } from '../../../../constants/TourCalendar';

type CalendarEventWrapperParams = {
  selectedEvent?: ZCalendarEvent;
  minEventDuration?: TourDuration;
  showCancelledTours?: boolean;
};
type CalendarEventProps = CalendarEventWrapperParams & EventProps<ZCalendarEvent>;

// Three 12px/16px lines (status label + time + renter name) need ~48px plus the tile's padding.
// A single-slot tile (~50px) only fits two, so require enough height to clear a third line; this
// sits between the 50px two-line tile and the next-taller tile RBC can render.
const MIN_TILE_HEIGHT_FOR_RENTER_NAME_PX = 60;

const CalendarEvent = (props: CalendarEventProps) => {
  const { event, title, minEventDuration = TourDuration.MINUTES_30, showCancelledTours = false } = props;
  const timeRange = useMemo(() => getTourEventTimeRange(event), [event]);
  const isTourEvent = event.eventType === Z_CALENDAR_EVENT_TYPE.TOUR;
  const isCancelledTour = isTourEvent && (event as TourEvent).tourStatus === TourStatusForZcheduler.CANCELLED;
  // Cancelled tiles only load when the flag is on, so they always show the name; requested/confirmed
  // tiles are always fetched, so their name is gated on the flag to stay unchanged while it's off.
  const renterName =
    isTourEvent && (isCancelledTour || showCancelledTours) ? (event as TourEvent).bookedByName : undefined;
  // Show the name only when the tile is tall enough for a third line. Tile height depends on the
  // tour duration AND the calendar granularity, so a 30-min tour fits in a 15-min calendar but not
  // in a 30-min one; shorter tiles keep the name in the click-through popover.
  const showRenterName =
    Boolean(renterName) &&
    getEventTileHeightPx(minEventDuration, differenceInMinutes(event.end, event.start)) >=
      MIN_TILE_HEIGHT_FOR_RENTER_NAME_PX;

  // Strike the time (and renter name, when shown) on cancelled tiles; the status label stays legible.
  const strikeClassName = isCancelledTour
    ? 'calendar-event-text calendar-event-strikethrough-text'
    : 'calendar-event-text';

  return (
    <Box css={{ height: '100%' }} data-testid={`CalendarEvent-${event.id}`}>
      <Paragraph css={{ whiteSpace: 'nowrap' }} className="calendar-event-text" textStyle="body-xs-bold">
        {title}
      </Paragraph>
      <Paragraph css={{ whiteSpace: 'nowrap' }} className={strikeClassName} textStyle="body-xs">
        {timeRange}
      </Paragraph>
      {showRenterName && (
        <Paragraph
          css={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          className={strikeClassName}
          textStyle="body-xs"
        >
          {renterName}
        </Paragraph>
      )}
    </Box>
  );
};

const CalendarEventWrapper = (params: CalendarEventWrapperParams) => (props: EventProps<ZCalendarEvent>) => {
  return <CalendarEvent {...params} {...props} />;
};

export default CalendarEventWrapper;
